import React, { useState, useEffect } from 'react';
import { useAttendance } from '../../contexts/AttendanceContext';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const AttendanceOptimizationTest = () => {
  const { user, isAuthenticated } = useAuth();
  const { 
    attendanceList, 
    pagination, 
    fetchAttendance, 
    fetchPaginatedAttendance,
    isLoading 
  } = useAttendance();
  
  const [testResults, setTestResults] = useState([]);
  const [isRunningTests, setIsRunningTests] = useState(false);

  const runTests = async () => {
    setIsRunningTests(true);
    setTestResults([]);
    
    // Check authentication first
    if (!isAuthenticated) {
      setTestResults([{
        name: 'Authentication Check',
        passed: false,
        message: 'User not authenticated. Please log in first.',
        timestamp: new Date().toISOString()
      }]);
      setIsRunningTests(false);
      toast.error('Please log in to run tests');
      return;
    }
    
    const tests = [
      {
        name: 'Test 1: Basic Data Loading',
        test: async () => {
          try {
            const result = await fetchAttendance();
            const attendanceData = result.attendance || [];
            return {
              passed: attendanceData.length > 0,
              message: `Loaded ${attendanceData.length} records`,
              data: { recordCount: attendanceData.length }
            };
          } catch (error) {
            return {
              passed: false,
              message: `Error: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      },
      {
        name: 'Test 2: Pagination Loading',
        test: async () => {
          try {
            const result = await fetchPaginatedAttendance({ page: 1, limit: 50 });
            
            // Use the returned data instead of state
            const attendanceData = result.attendance || [];
            const paginationData = result.pagination || {};
            
            // Check if pagination is working correctly
            const isPaginated = attendanceData.length <= 50;
            const hasPaginationData = paginationData.totalRecords > 0;
            const isCorrectPage = paginationData.currentPage === 1;
            
            return {
              passed: isPaginated && hasPaginationData && isCorrectPage,
              message: `Paginated: ${attendanceData.length} records, Total: ${paginationData.totalRecords}, Page: ${paginationData.currentPage}`,
              data: { 
                currentRecords: attendanceData.length,
                totalRecords: paginationData.totalRecords,
                currentPage: paginationData.currentPage,
                isPaginated: isPaginated,
                hasPaginationData: hasPaginationData,
                isCorrectPage: isCorrectPage
              }
            };
          } catch (error) {
              return {
                passed: false,
                message: `Error: ${error.message}`,
                data: { error: error.message }
              };
          }
        }
      },
      {
        name: 'Test 3: Date Filtering',
        test: async () => {
          try {
            const startDate = new Date();
            startDate.setDate(startDate.getDate() - 30);
            const endDate = new Date();
            
            const result = await fetchAttendance({
              startDate: startDate.toISOString().split('T')[0],
              endDate: endDate.toISOString().split('T')[0]
            });
            
            const attendanceData = result.attendance || [];
            
            return {
              passed: attendanceData.length >= 0,
              message: `Date filtered: ${attendanceData.length} records`,
              data: { 
                recordCount: attendanceData.length,
                startDate: startDate.toISOString().split('T')[0],
                endDate: endDate.toISOString().split('T')[0]
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `Error: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      },
      {
        name: 'Test 4: Large Dataset Export',
        test: async () => {
          try {
            const startTime = Date.now();
            const result = await fetchAttendance({ page: 1, limit: 10000 });
            const endTime = Date.now();
            const responseTime = endTime - startTime;
            
            const attendanceData = result.attendance || [];
            
            return {
              passed: responseTime < 10000 && attendanceData.length > 0, // Less than 10 seconds
              message: `Large dataset: ${attendanceData.length} records in ${responseTime}ms`,
              data: { 
                recordCount: attendanceData.length,
                responseTime: responseTime
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `Error: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      },
      {
        name: 'Test 6: Production Stress Test',
        test: async () => {
          try {
            // Test with high limit to simulate production load
            const startTime = Date.now();
            const result = await fetchAttendance({ page: 1, limit: 5000 });
            const endTime = Date.now();
            const responseTime = endTime - startTime;
            
            const attendanceData = result.attendance || [];
            const paginationData = result.pagination || {};
            
            // Check if performance is acceptable for production
            const isPerformanceGood = responseTime < 5000; // Less than 5 seconds
            const hasData = attendanceData.length > 0;
            const hasPagination = paginationData.totalRecords > 0;
            
            return {
              passed: isPerformanceGood && hasData && hasPagination,
              message: `Production stress test: ${attendanceData.length} records in ${responseTime}ms (Total: ${paginationData.totalRecords})`,
              data: { 
                recordCount: attendanceData.length,
                totalRecords: paginationData.totalRecords,
                responseTime: responseTime,
                performanceGood: isPerformanceGood
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `Error: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      },
      {
        name: 'Test 7: Pagination Performance',
        test: async () => {
          try {
            // Test multiple pages to ensure pagination works at scale
            const startTime = Date.now();
            const result = await fetchPaginatedAttendance({ page: 1, limit: 100 });
            const endTime = Date.now();
            const responseTime = endTime - startTime;
            
            const attendanceData = result.attendance || [];
            const paginationData = result.pagination || {};
            
            // Check if we get exactly 100 records and performance is good
            const isCorrectLimit = attendanceData.length <= 100;
            const isPerformanceGood = responseTime < 2000; // Less than 2 seconds
            const hasPagination = paginationData.totalRecords > 0;
            
            return {
              passed: isCorrectLimit && isPerformanceGood && hasPagination,
              message: `Pagination performance: ${attendanceData.length} records in ${responseTime}ms (Total: ${paginationData.totalRecords})`,
              data: { 
                recordCount: attendanceData.length,
                totalRecords: paginationData.totalRecords,
                responseTime: responseTime,
                correctLimit: isCorrectLimit,
                performanceGood: isPerformanceGood
              }
            };
          } catch (error) {
            return {
              passed: false,
              message: `Error: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      },
      {
        name: 'Test 5: Pagination Navigation',
        test: async () => {
          try {
            if (pagination.totalPages > 1) {
              const result = await fetchPaginatedAttendance({ page: 2, limit: 50 });
              const paginationData = result.pagination || {};
              const attendanceData = result.attendance || [];
              
              return {
                passed: paginationData.currentPage === 2,
                message: `Navigated to page 2: ${attendanceData.length} records`,
                data: { 
                  currentPage: paginationData.currentPage,
                  recordCount: attendanceData.length
                }
              };
            } else {
              return {
                passed: true,
                message: 'Only 1 page available, skipping navigation test',
                data: { skipped: true }
              };
            }
          } catch (error) {
            return {
              passed: false,
              message: `Error: ${error.message}`,
              data: { error: error.message }
            };
          }
        }
      }
    ];

    const results = [];
    
    for (const test of tests) {
      try {
        console.log(`🧪 Running: ${test.name}`);
        const result = await test.test();
        results.push({
          ...result,
          name: test.name,
          timestamp: new Date().toISOString()
        });
        
        console.log(`✅ ${test.name}: ${result.message}`);
      } catch (error) {
        console.error(`❌ ${test.name}: ${error.message}`);
        results.push({
          name: test.name,
          passed: false,
          message: `Error: ${error.message}`,
          timestamp: new Date().toISOString()
        });
      }
    }
    
    setTestResults(results);
    setIsRunningTests(false);
    
    const passedTests = results.filter(r => r.passed).length;
    const totalTests = results.length;
    
    if (passedTests === totalTests) {
      toast.success(`All ${totalTests} tests passed! 🎉`);
    } else {
      toast.error(`${passedTests}/${totalTests} tests passed`);
    }
  };

  return (
    <div className="card p-6">
      <h2 className="text-2xl font-bold mb-4">🧪 Attendance Optimization Test Suite</h2>
      
      {/* Production Readiness Warning */}
      <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <h3 className="font-semibold text-yellow-800 mb-2">⚠️ Production Readiness Checklist:</h3>
        <ul className="text-sm text-yellow-700 space-y-1">
          <li>• Database indexing on employeeId, stepIn, managerId</li>
          <li>• MongoDB connection pooling configured</li>
          <li>• Server memory allocation for large datasets</li>
          <li>• Network timeout settings (30+ seconds for exports)</li>
          <li>• Error handling for database connection failures</li>
          <li>• Rate limiting for API endpoints</li>
          <li>• Monitoring and alerting for slow queries</li>
        </ul>
      </div>
      
      <div className="mb-6">
        <button
          onClick={runTests}
          disabled={isRunningTests}
          className="btn btn-primary"
        >
          {isRunningTests ? 'Running Tests...' : 'Run All Tests'}
        </button>
      </div>

      {/* Current Data Status */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card p-4">
          <h3 className="font-semibold">Authentication</h3>
          <p className="text-sm text-gray-600">
            Status: {isAuthenticated ? '✅ Authenticated' : '❌ Not Authenticated'}
          </p>
          <p className="text-sm text-gray-600">
            User: {user?.name || 'N/A'}
          </p>
        </div>
        <div className="card p-4">
          <h3 className="font-semibold">Current Data</h3>
          <p className="text-sm text-gray-600">
            Records: {attendanceList.length}
          </p>
          <p className="text-sm text-gray-600">
            Loading: {isLoading ? 'Yes' : 'No'}
          </p>
        </div>
        
        <div className="card p-4">
          <h3 className="font-semibold">Pagination</h3>
          <p className="text-sm text-gray-600">
            Page: {pagination.currentPage}/{pagination.totalPages}
          </p>
          <p className="text-sm text-gray-600">
            Total: {pagination.totalRecords}
          </p>
        </div>
        
        <div className="card p-4">
          <h3 className="font-semibold">Test Results</h3>
          <p className="text-sm text-gray-600">
            Tests: {testResults.length}
          </p>
          <p className="text-sm text-gray-600">
            Passed: {testResults.filter(r => r.passed).length}
          </p>
        </div>
      </div>

      {/* Test Results */}
      {testResults.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Test Results</h3>
          {testResults.map((result, index) => (
            <div key={index} className={`card p-4 ${result.passed ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">{result.name}</h4>
                  <p className="text-sm text-gray-600">{result.message}</p>
                  {result.data && (
                    <div className="mt-2 text-xs text-gray-500">
                      <pre>{JSON.stringify(result.data, null, 2)}</pre>
                    </div>
                  )}
                </div>
                <div className={`badge ${result.passed ? 'badge-success' : 'badge-error'}`}>
                  {result.passed ? 'PASS' : 'FAIL'}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Debug Information */}
      <div className="mt-6">
        <h3 className="text-lg font-semibold mb-2">Debug Information</h3>
        <div className="bg-gray-100 p-4 rounded">
          <pre className="text-xs overflow-auto">
            {JSON.stringify({
              attendanceListLength: attendanceList.length,
              pagination,
              isLoading,
              timestamp: new Date().toISOString()
            }, null, 2)}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default AttendanceOptimizationTest;
